import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Gift, RefreshCw, Save, Trash2, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ScheduledPromos } from './ScheduledPromos';
import { WinbackTriggers } from './WinbackTriggers';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const FreePlayTab = ({
    users,
    promoSelectedUserIds,
    setPromoSelectedUserIds,
    promoUserSearch,
    setPromoUserSearch,
    promoAmount,
    setPromoAmount,
    promoReason,
    setPromoReason,
    promoLoading,
    promoReport,
    promoHistory,
    fetchPromoData,
    handleGivePromo,
    setConfirmAction,
    setConfirmDialogOpen,
    promoTemplates,
    fetchPromoTemplates,
}) => {
    const [saveTplOpen, setSaveTplOpen] = useState(false);
    const [tplName, setTplName] = useState('');
    const [tplSaving, setTplSaving] = useState(false);

    const filteredVisible = users
        .filter(u => u.role !== 'admin' && (u.status === 'active' || !u.status))
        .filter(u => {
            const q = promoUserSearch.toLowerCase().trim();
            if (!q) return true;
            return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
        });

    const handleSelectAllVisible = () => {
        setPromoSelectedUserIds(filteredVisible.map(u => u.id));
    };

    const handleClearSelection = () => setPromoSelectedUserIds([]);

    const handleInvertVisible = () => {
        const visibleIds = new Set(filteredVisible.map(u => u.id));
        const next = [];
        for (const id of visibleIds) {
            if (!promoSelectedUserIds.includes(id)) next.push(id);
        }
        for (const id of promoSelectedUserIds) {
            if (!visibleIds.has(id)) next.push(id);
        }
        setPromoSelectedUserIds(next);
    };

    const handleToggleUser = (id, checked) => {
        if (checked) {
            setPromoSelectedUserIds([...promoSelectedUserIds, id]);
        } else {
            setPromoSelectedUserIds(promoSelectedUserIds.filter(x => x !== id));
        }
    };

    const onSubmit = () => {
        const target = promoSelectedUserIds.length > 0 ? promoSelectedUserIds : null;
        const label = target ? `${target.length} selected player(s)` : 'ALL active users';
        setConfirmAction({
            type: 'load',
            message: `Give $${promoAmount} free play credits to ${label}?`,
            onConfirm: async () => {
                await handleGivePromo(target);
                setConfirmDialogOpen(false);
                setConfirmAction(null);
            },
        });
        setConfirmDialogOpen(true);
    };

    const applyTemplate = (tpl) => {
        setPromoAmount(String(tpl.amount));
        setPromoReason(tpl.reason || '');
        toast.success(`Loaded template: ${tpl.name}`);
    };

    const handleSaveTemplate = async () => {
        if (!tplName.trim()) { toast.error('Enter a template name'); return; }
        const amt = parseFloat(promoAmount);
        if (!amt || amt <= 0) { toast.error('Enter a valid amount first'); return; }
        setTplSaving(true);
        try {
            await axios.post(`${API}/admin/promo-templates`, {
                name: tplName.trim(),
                amount: amt,
                reason: promoReason || 'Promotional credits',
            }, { withCredentials: true });
            toast.success(`Saved template: ${tplName.trim()}`);
            setSaveTplOpen(false);
            setTplName('');
            fetchPromoTemplates();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save template');
        }
        setTplSaving(false);
    };

    const handleDeleteTemplate = (tpl) => {
        setConfirmAction({
            type: 'delete',
            message: `Delete template "${tpl.name}"? This cannot be undone.`,
            onConfirm: async () => {
                try {
                    await axios.delete(`${API}/admin/promo-templates/${tpl.id}`, { withCredentials: true });
                    toast.success('Template deleted');
                    fetchPromoTemplates();
                } catch (e) {
                    toast.error('Failed to delete template');
                }
                setConfirmDialogOpen(false);
                setConfirmAction(null);
            },
        });
        setConfirmDialogOpen(true);
    };

    return (
        <div className="space-y-6">
            {/* Win-Back Triggers (activity-based auto-promos) */}
            <WinbackTriggers
                setConfirmAction={setConfirmAction}
                setConfirmDialogOpen={setConfirmDialogOpen}
            />

            {/* Recurring / Scheduled Promos */}
            <ScheduledPromos
                setConfirmAction={setConfirmAction}
                setConfirmDialogOpen={setConfirmDialogOpen}
            />

            {/* Quick-Pick Templates */}
            <div className="card-dark p-6" data-testid="promo-templates-panel">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#FFD700]" />
                        <h3 className="text-base font-bold text-white">Quick-Pick Templates</h3>
                    </div>
                    <span className="text-[10px] text-[#A0AAB2]">{(promoTemplates || []).length} saved</span>
                </div>
                {(!promoTemplates || promoTemplates.length === 0) ? (
                    <p className="text-[#A0AAB2] text-xs">
                        No templates yet. Fill in an amount + reason below, then click <span className="text-[#FFD700] font-bold">Save as Template</span> to reuse it later.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {promoTemplates.map(tpl => (
                            <div key={tpl.id}
                                className="flex items-stretch border border-[#FFD700]/30 rounded-full overflow-hidden hover:border-[#FFD700] transition-colors"
                                data-testid={`promo-template-${tpl.id}`}>
                                <button
                                    type="button"
                                    onClick={() => applyTemplate(tpl)}
                                    className="px-3 py-1.5 text-xs bg-[#FFD700]/10 hover:bg-[#FFD700]/20 text-white"
                                    data-testid={`promo-template-apply-${tpl.id}`}
                                >
                                    <span className="font-bold text-[#FFD700]">${tpl.amount.toFixed(2)}</span>
                                    <span className="mx-1.5 text-[#A0AAB2]">·</span>
                                    <span>{tpl.name}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleDeleteTemplate(tpl)}
                                    className="px-2 bg-[#0a0a0a] hover:bg-[#FF3B30]/20 text-[#A0AAB2] hover:text-[#FF3B30] border-l border-[#FFD700]/30"
                                    title="Delete template"
                                    data-testid={`promo-template-delete-${tpl.id}`}
                                >
                                    <Trash2 className="w-3 h-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Give Promo */}
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">Give Free Play / Promo Credits</h3>
                <div className="space-y-3">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Label className="text-[#A0AAB2] text-xs">Recipients</Label>
                            <span className="text-[10px] text-[#FFD700] font-mono" data-testid="promo-selected-count">
                                {promoSelectedUserIds.length === 0 ? 'ALL ACTIVE USERS' : `${promoSelectedUserIds.length} selected`}
                            </span>
                        </div>
                        <Input
                            value={promoUserSearch}
                            onChange={e => setPromoUserSearch(e.target.value)}
                            placeholder="Search by name or email..."
                            className="bg-[#0a0a0a] border-white/10 text-white mb-2"
                            data-testid="promo-user-search"
                        />
                        <div className="flex flex-wrap gap-2 mb-2">
                            <Button size="sm" variant="outline" onClick={handleSelectAllVisible}
                                className="border-[#FFD700]/40 text-[#FFD700] text-xs h-7"
                                data-testid="promo-select-all-btn">
                                Select All Visible
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleClearSelection}
                                className="border-white/20 text-white text-xs h-7"
                                data-testid="promo-clear-selection-btn">
                                Clear Selection
                            </Button>
                            <Button size="sm" variant="outline" onClick={handleInvertVisible}
                                className="border-white/20 text-white text-xs h-7"
                                data-testid="promo-invert-selection-btn">
                                Invert Visible
                            </Button>
                        </div>
                        <div className="border border-white/10 rounded-lg max-h-60 overflow-y-auto bg-[#0a0a0a]" data-testid="promo-users-list">
                            {filteredVisible.length === 0 ? (
                                <p className="text-center text-[#A0AAB2] text-sm py-6">No active customers match.</p>
                            ) : filteredVisible.map(u => {
                                const checked = promoSelectedUserIds.includes(u.id);
                                return (
                                    <label
                                        key={u.id}
                                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5 border-b border-white/5 last:border-b-0 ${checked ? 'bg-[#FFD700]/10' : ''}`}
                                        data-testid={`promo-user-row-${u.id}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={(e) => handleToggleUser(u.id, e.target.checked)}
                                            className="accent-[#FFD700] w-4 h-4"
                                            data-testid={`promo-user-checkbox-${u.id}`}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-white text-sm truncate">{u.name}</p>
                                            <p className="text-[10px] text-[#A0AAB2] truncate">{u.email}</p>
                                        </div>
                                        <span className="text-[10px] text-[#FFD700] font-mono">${(u.credits || 0).toFixed(2)}</span>
                                    </label>
                                );
                            })}
                        </div>
                        <p className="text-[10px] text-[#A0AAB2] mt-2">
                            Tip: leave selection empty to send to <span className="text-[#FFD700] font-bold">ALL active users</span>.
                        </p>
                    </div>
                    <div>
                        <Label className="text-[#A0AAB2] text-xs">Amount</Label>
                        <Input type="number" value={promoAmount} onChange={e => setPromoAmount(e.target.value)}
                            placeholder="e.g. 1.00" className="bg-[#0a0a0a] border-white/10 text-white mt-1" data-testid="promo-amount" />
                    </div>
                    <div>
                        <Label className="text-[#A0AAB2] text-xs">Reason</Label>
                        <Input value={promoReason} onChange={e => setPromoReason(e.target.value)}
                            placeholder="e.g. Welcome bonus, Weekly promo" className="bg-[#0a0a0a] border-white/10 text-white mt-1" data-testid="promo-reason" />
                    </div>
                    <Button onClick={onSubmit} disabled={promoLoading || !promoAmount}
                        className="w-full bg-[#A855F7] text-white font-bold" data-testid="give-promo-btn">
                        {promoLoading ? 'Sending...' : <><Gift className="w-4 h-4 mr-2" /> Give Free Play</>}
                    </Button>
                    <Button onClick={() => setSaveTplOpen(true)} disabled={!promoAmount}
                        variant="outline"
                        className="w-full border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 text-xs h-8"
                        data-testid="save-template-btn">
                        <Save className="w-3 h-3 mr-1.5" /> Save Current Amount + Reason as Template
                    </Button>
                </div>
            </div>

            {/* Promo Report */}
            <div className="card-dark p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Free Play Report</h3>
                    <Button size="sm" variant="outline" onClick={fetchPromoData} className="border-[#FFD700]/30 text-[#FFD700]">
                        <RefreshCw className="w-3 h-3 mr-1" />Load
                    </Button>
                </div>
                {promoReport ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        {['today', 'week', 'month', 'year'].map(period => (
                            <div key={period} className="bg-[#0a0a0a] rounded-xl p-3 text-center">
                                <p className="text-[10px] font-bold text-[#A0AAB2] uppercase">{period === 'today' ? 'Today' : period === 'week' ? 'This Week' : period === 'month' ? 'This Month' : 'This Year'}</p>
                                <p className="text-xl font-black text-[#A855F7] font-mono">{(promoReport[period]?.total || 0).toFixed(2)}</p>
                                <p className="text-[10px] text-[#666]">{promoReport[period]?.count || 0} promos</p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-[#A0AAB2] text-center py-4 text-sm">Click Load to see promo report</p>
                )}
            </div>

            {/* Promo History */}
            <div className="card-dark p-6">
                <h3 className="text-lg font-bold text-white mb-4">Free Play History ({promoHistory.length})</h3>
                {promoHistory.length === 0 ? (
                    <p className="text-[#A0AAB2] text-center py-4 text-sm">No promos given yet</p>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-white/10">
                                    <TableHead className="text-[#A0AAB2]">Customer</TableHead>
                                    <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Reason</TableHead>
                                    <TableHead className="text-[#A0AAB2]">Date</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {promoHistory.map((p, i) => (
                                    <TableRow key={i} className="border-white/5">
                                        <TableCell>
                                            <p className="text-white text-sm">{p.user_name}</p>
                                            <p className="text-[10px] text-[#A0AAB2]">{p.user_email}</p>
                                        </TableCell>
                                        <TableCell className="text-right font-mono text-[#A855F7] font-bold">+{p.amount.toFixed(2)}</TableCell>
                                        <TableCell className="text-xs text-[#A0AAB2]">{p.reason}</TableCell>
                                        <TableCell className="text-xs text-[#A0AAB2]">
                                            {new Date(p.created_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            {/* Save as Template Dialog */}
            <Dialog open={saveTplOpen} onOpenChange={setSaveTplOpen}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white">Save as Template</DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Save the current amount + reason as a reusable quick-pick template.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-[#A0AAB2] text-xs">Template Name</Label>
                            <Input value={tplName} onChange={e => setTplName(e.target.value)}
                                placeholder='e.g. "Welcome $5", "Sunday Special"'
                                className="bg-[#0a0a0a] border-white/10 text-white mt-1"
                                data-testid="save-template-name-input"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveTemplate(); }}
                            />
                        </div>
                        <div className="bg-[#0a0a0a] rounded-lg p-3 text-xs">
                            <div className="flex justify-between text-[#A0AAB2]"><span>Amount</span><span className="text-[#FFD700] font-mono">${promoAmount || '0.00'}</span></div>
                            <div className="flex justify-between text-[#A0AAB2] mt-1"><span>Reason</span><span className="text-white">{promoReason || 'Promotional credits'}</span></div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSaveTplOpen(false)} disabled={tplSaving}
                            className="border-white/20 text-white">Cancel</Button>
                        <Button onClick={handleSaveTemplate} disabled={tplSaving || !tplName.trim()}
                            className="bg-[#FFD700] text-black font-bold" data-testid="save-template-confirm-btn">
                            {tplSaving ? 'Saving...' : <><Save className="w-3 h-3 mr-1.5" />Save Template</>}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
